import React, { useState, useRef, useEffect } from 'react';
import { TruncatedText } from './TruncatedText';
import { APP_CONFIG, formatDate } from '../../config/appConfig';
import { evaluateCondition } from '../../utils/expressionEvaluator';
import { ConditionalFormattingRule } from '../../services/conditionalFormattingService';
import { cn, mergeTailwindClasses } from '../../lib/utils';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';

export type FooterType = 'sum' | 'avg' | 'count' | 'custom';

export interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  /** Optional maximum width for this column (e.g. '200px') used to constrain very wide cells. */
  maxWidth?: string;
  align?: 'left' | 'center' | 'right';
  // Text truncation options
  maxLength?: number; // Maximum character length before truncation
  maxLines?: number; // Maximum number of lines before truncation (uses line-clamp)
  showTooltip?: boolean; // Show tooltip/popover with full content on focus (default: true)
  // Footer options
  footerType?: FooterType; // Type of footer calculation: 'sum', 'avg', 'count', or 'custom'
  footerRender?: (value: any, allData: T[]) => React.ReactNode; // Custom footer render function (used when footerType is 'custom')
  footerLabel?: string; // Custom label for footer (defaults to footerType)
}

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  pagination?: PaginationProps;
  sorting?: {
    sortConfig: SortConfig | null;
    onSort: (key: string) => void;
  };
  actions?: (row: T) => React.ReactNode;
  actionsPosition?: 'left' | 'right';
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  keyExtractor: (row: T) => string | number;
  density?: 'compact' | 'normal' | 'comfortable';
  conditionalFormattingRules?: ConditionalFormattingRule[];
  showFooter?: boolean; // Enable/disable footer row
  footerData?: T[]; // Optional data to use for footer calculations (defaults to all data if not provided)
  selectedRowId?: string | number | null; // ID of the currently selected row for highlighting
  /** Optional class overrides for a custom table look (e.g. portal) */
  containerClassName?: string;
  tableClassName?: string;
  theadClassName?: string;
  tbodyClassName?: string;
  thClassName?: string;
  trClassName?: string;
  tdClassName?: string;
  /** When set (e.g. 'w-full'), table wrapper fills container width so columns span full width */
  tableWrapperClassName?: string;
  /** When true, keep header row visible while scrolling table body */
  stickyHeader?: boolean;
}

function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  pagination,
  sorting,
  actions,
  actionsPosition = 'right',
  emptyMessage = 'No data available',
  onRowClick,
  keyExtractor,
  density,
  conditionalFormattingRules = [],
  showFooter = false,
  footerData,
  selectedRowId,
  containerClassName,
  tableClassName,
  theadClassName,
  tbodyClassName,
  thClassName,
  trClassName,
  tdClassName,
  tableWrapperClassName,
  stickyHeader = true,
}: DataTableProps<T>) {
  const { colors } = useTheme();
  const { appConfig } = useSettings();
  const effectiveDensity = density || appConfig.table.defaultDensity;
  const densityConfig = APP_CONFIG.table.densityConfig[effectiveDensity] || APP_CONFIG.table.densityConfig[appConfig.table.defaultDensity];
  const [itemsPerPage, setItemsPerPage] = useState(pagination?.itemsPerPage || appConfig.table.defaultPageSize);
  const pageSizeOptions = appConfig.table.pageSizeOptions;
  const mainTableRef = useRef<HTMLTableElement>(null);
  const footerTableRef = useRef<HTMLTableElement>(null);

  // Data to use for footer calculations (use footerData if provided, otherwise use all data)
  const footerCalculationData = footerData || data;

  // Sync footer table column widths with main table
  useEffect(() => {
    if (showFooter && mainTableRef.current && footerTableRef.current) {
      const syncWidths = () => {
        const mainTable = mainTableRef.current;
        const footerTable = footerTableRef.current;
        
        if (!mainTable || !footerTable) return;
        
        // Get all header cells from main table (including actions)
        const mainHeaderCells = mainTable.querySelectorAll('thead tr:first-child th');
        const footerCells = footerTable.querySelectorAll('tbody tr:first-child td');
        
        // Sync widths from main table headers to footer cells
        mainHeaderCells.forEach((mainCell, index) => {
          if (footerCells[index]) {
            const mainCellElement = mainCell as HTMLElement;
            const footerCellElement = footerCells[index] as HTMLElement;
            
            // Get the actual rendered width
            const width = mainCellElement.offsetWidth;
            
            if (width > 0) {
              // Set width on both the cell and the col element
              footerCellElement.style.width = `${width}px`;
              footerCellElement.style.minWidth = `${width}px`;
              footerCellElement.style.maxWidth = `${width}px`;
              
              // Also update the colgroup col element
              const footerColgroup = footerTable.querySelector('colgroup');
              if (footerColgroup) {
                const footerCols = footerColgroup.querySelectorAll('col');
                if (footerCols[index]) {
                  (footerCols[index] as HTMLElement).style.width = `${width}px`;
                  (footerCols[index] as HTMLElement).style.minWidth = `${width}px`;
                  (footerCols[index] as HTMLElement).style.maxWidth = `${width}px`;
                }
              }
            }
          }
        });
      };
      
      // Sync immediately
      syncWidths();
      
      // Also sync after a short delay to ensure main table is fully rendered
      const timeoutId1 = setTimeout(syncWidths, 10);
      const timeoutId2 = setTimeout(syncWidths, 100);
      
      // Sync on window resize
      window.addEventListener('resize', syncWidths);
      
      // Use ResizeObserver to sync when main table resizes
      let resizeObserver: ResizeObserver | null = null;
      if (mainTableRef.current) {
        resizeObserver = new ResizeObserver(syncWidths);
        resizeObserver.observe(mainTableRef.current);
      }
      
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        window.removeEventListener('resize', syncWidths);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, [showFooter, data, columns]);

  const handleSort = (key: string) => {
    if (sorting && columns.find((col) => col.key === key)?.sortable) {
      sorting.onSort(key);
    }
  };

  const getSortIcon = (key: string) => {
    if (!sorting || !sorting.sortConfig || sorting.sortConfig.key !== key) {
      return (
        <svg className={`w-4 h-4 ${colors.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    if (sorting.sortConfig.direction === 'asc') {
      return (
        <svg className={`w-4 h-4 text-indigo-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }

    return (
      <svg className={`w-4 h-4 text-indigo-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Helper: Get matching column-level formatting rules
  const getColumnFormattingRules = (columnKey: string, cellValue: any, row: T): ConditionalFormattingRule[] => {
    return conditionalFormattingRules
      .filter(rule => rule.isActive && rule.scope === 'column')
      .filter(rule => {
        // For column scope, check if it applies to this column
        if (rule.condition.type === 'column') {
          return rule.condition.columnKey === columnKey || rule.columnKey === columnKey;
        } else {
          // Expression-based rules apply to the reference column
          return rule.columnKey === columnKey;
        }
      })
      .filter(rule => {
        return evaluateCondition(rule.condition, row as Record<string, any>, columnKey);
      })
      .sort((a, b) => b.priority - a.priority);
  };

  // Helper: Get matching row-level formatting rules
  const getRowFormattingRules = (row: T): ConditionalFormattingRule[] => {
    return conditionalFormattingRules
      .filter(rule => rule.isActive && rule.scope === 'row')
      .filter(rule => {
        return evaluateCondition(rule.condition, row as Record<string, any>, rule.columnKey);
      })
      .sort((a, b) => b.priority - a.priority);
  };

  // Helper: Combine styles from multiple rules
  const combineStyles = (rules: ConditionalFormattingRule[]): string => {
    if (rules.length === 0) return '';
    
    const styleParts: string[] = [];
    rules.forEach(rule => {
      if (rule.style.backgroundColor) styleParts.push(rule.style.backgroundColor);
      if (rule.style.textColor) styleParts.push(rule.style.textColor);
      if (rule.style.fontWeight) styleParts.push(rule.style.fontWeight);
      if (rule.style.borderColor) styleParts.push(rule.style.borderColor);
    });
    
    return styleParts.join(' ');
  };

  // Calculate footer value for a column
  const calculateFooterValue = (column: Column<T>): React.ReactNode => {
    if (!column.footerType) return null;

    switch (column.footerType) {
      case 'sum': {
        const values = footerCalculationData
          .map(row => {
            const value = column.key in row ? row[column.key as keyof T] : null;
            // Try to parse as number
            if (value === null || value === undefined) return 0;
            const numValue = typeof value === 'number' ? value : parseFloat(String(value));
            return isNaN(numValue) ? 0 : numValue;
          })
          .filter(v => !isNaN(v));
        
        const sum = values.reduce((acc, val) => acc + val, 0);
        
        // Use custom render if provided, otherwise format as number
        if (column.footerRender) {
          return column.footerRender(sum, footerCalculationData);
        }
        
        // Format number with locale
        return typeof sum === 'number' && !isNaN(sum)
          ? sum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : '-';
      }
      
      case 'avg': {
        const values = footerCalculationData
          .map(row => {
            const value = column.key in row ? row[column.key as keyof T] : null;
            if (value === null || value === undefined) return null;
            const numValue = typeof value === 'number' ? value : parseFloat(String(value));
            return isNaN(numValue) ? null : numValue;
          })
          .filter((v): v is number => v !== null && !isNaN(v));
        
        if (values.length === 0) return '-';
        
        const avg = values.reduce((acc, val) => acc + val, 0) / values.length;
        
        // Use custom render if provided, otherwise format as number
        if (column.footerRender) {
          return column.footerRender(avg, footerCalculationData);
        }
        
        return avg.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      
      case 'count': {
        const count = footerCalculationData.length;
        
        // Use custom render if provided, otherwise show count
        if (column.footerRender) {
          return column.footerRender(count, footerCalculationData);
        }
        
        return count.toLocaleString('en-US');
      }
      
      case 'custom': {
        if (column.footerRender) {
          return column.footerRender(null, footerCalculationData);
        }
        return '-';
      }
      
      default:
        return null;
    }
  };

  // Get footer label for a column
  const getFooterLabel = (column: Column<T>): string => {
    if (column.footerLabel) return column.footerLabel;
    
    switch (column.footerType) {
      case 'sum':
        return 'Sum';
      case 'avg':
        return 'Avg';
      case 'count':
        return 'Count';
      case 'custom':
        return '';
      default:
        return '';
    }
  };

  const renderCell = (column: Column<T>, row: T) => {
    const value = column.key in row ? row[column.key as keyof T] : null;

    // Get column-level formatting rules
    const columnRules = getColumnFormattingRules(String(column.key), value, row);
    const columnStyles = combineStyles(columnRules);

    let cellContent;
    if (column.render) {
      cellContent = column.render(value, row);
    } else {
      // Default rendering
      // Handle objects - convert to string or extract meaningful value
      let displayValue: any = value;
      if (typeof value === 'object' && value !== null && !React.isValidElement(value)) {
        // If it's an object with common description fields, extract them
        if ('pst_descr' in value) {
          displayValue = (value as any).pst_descr || JSON.stringify(value);
        } else if ('pit_descr' in value) {
          displayValue = (value as any).pit_descr || JSON.stringify(value);
        } else if ('pic_descr' in value) {
          displayValue = (value as any).pic_descr || JSON.stringify(value);
        } else if ('pts_descr' in value) {
          displayValue = (value as any).pts_descr || JSON.stringify(value);
        } else if ('ts_descr' in value) {
          displayValue = (value as any).ts_descr || JSON.stringify(value);
        } else {
          displayValue = JSON.stringify(value);
        }
      }
      if (displayValue === null || displayValue === undefined) {
        return <span className={colors.textMuted}>-</span>;
      }

      if (typeof displayValue === 'boolean') {
        return (
          <span
            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              displayValue ? colors.success : colors.error
            }`}
          >
            {displayValue ? 'Yes' : 'No'}
          </span>
        );
      }

      // Check if displayValue is a Date object or date string
      if (typeof displayValue === 'object' && displayValue !== null && displayValue.constructor === Date) {
        return formatDate(displayValue);
      }
      
      if (typeof displayValue === 'string' && displayValue.match(/^\d{4}-\d{2}-\d{2}/)) {
        return formatDate(displayValue);
      }

      // If column has truncation options and displayValue is a string, use TruncatedText
      if (typeof displayValue === 'string' && (column.maxLength || column.maxLines)) {
        return (
          <TruncatedText
            text={displayValue}
            maxLength={column.maxLength}
            maxLines={column.maxLines}
            className={`text-[13px] leading-snug ${colors.text}`}
            showTooltip={column.showTooltip !== false}
          />
        );
      }

      cellContent = String(displayValue);
    }

    // Apply column-level styles to both custom render and default content
    if (columnStyles) {
      // If cellContent is already a React element, try to clone it and add classes
      if (React.isValidElement(cellContent)) {
        // Clone the element and merge className props using smart Tailwind merging
        const existingClassName = (cellContent.props as any)?.className || '';
        const mergedClassName = mergeTailwindClasses(existingClassName, columnStyles);
        
        return React.cloneElement(cellContent as React.ReactElement<any>, {
          className: mergedClassName,
        } as any);
      }
      // Otherwise, wrap in a span with styles
      return (
        <span className={columnStyles}>
          {cellContent}
        </span>
      );
    }

    // If cellContent is a plain string, wrap it to ensure it uses the correct text color
    if (typeof cellContent === 'string') {
      return <span className={colors.text}>{cellContent}</span>;
    }

    return cellContent;
  };

  if (loading) {
    return (
      <div className={`${colors.surface} rounded-lg shadow-sm border ${colors.border} p-8`}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className={`mt-2 ${colors.textSecondary}`}>Loading data...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={`${colors.surface} rounded-lg shadow-sm border ${colors.border} p-8`}>
        <div className="text-center">
          <svg
            className={`mx-auto h-12 w-12 ${colors.textMuted}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className={`mt-2 text-sm font-medium ${colors.text}`}>No data</h3>
          <p className={`mt-1 text-sm ${colors.textMuted}`}>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Generate colgroup for consistent column widths
  const colgroup = (
    <colgroup>
      {actions && actionsPosition === 'left' && <col style={{ width: 'auto' }} />}
      {columns.map((column) => (
        <col
          key={String(column.key)}
          style={{
            width: column.width || 'auto',
            // Allow presets to cap how wide a column can grow
            maxWidth: column.maxWidth,
          }}
        />
      ))}
      {actions && actionsPosition === 'right' && <col style={{ width: 'auto' }} />}
    </colgroup>
  );

  return (
    <div className={cn('h-full flex flex-col overflow-hidden', colors.surface, 'rounded-lg shadow-sm border', colors.border, containerClassName)}>
      <div className="flex-1 min-h-0 overflow-auto dashboard-scrollbar">
        <div className={cn('align-middle', tableWrapperClassName ?? 'inline-block min-w-max')}>
          <table ref={mainTableRef} className={cn('min-w-full', !tableClassName && `divide-y ${colors.border}`, tableClassName)} style={{ tableLayout: 'auto' }}>
            {colgroup}
          <thead
            className={cn(
              colors.surfaceHover,
              stickyHeader && 'sticky top-0 z-10',
              theadClassName
            )}
          >
            <tr>
              {actions && actionsPosition === 'left' && (
                <th
                  scope="col"
                  className={cn(densityConfig.headerPadding, 'text-right text-[11px] font-semibold', colors.textSecondary, 'uppercase tracking-wide', thClassName)}
                >
                  Actions
                </th>
              )}
              {columns.map((column, colIndex) => (
                <th
                  key={String(column.key)}
                  scope="col"
                  className={cn(
                    densityConfig.headerPadding,
                    'text-left text-[11px] font-semibold',
                    colors.textSecondary,
                    'uppercase tracking-wide',
                    column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left',
                    thClassName
                  )}
                >
                  <div
                    className={`flex items-center space-x-1 ${
                      column.sortable && sorting
                        ? `cursor-pointer hover:${colors.textSecondary} select-none`
                        : ''
                    } ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : ''}`}
                    onClick={() => column.sortable && handleSort(String(column.key))}
                  >
                    <span>{column.header}</span>
                    {column.sortable && sorting && getSortIcon(String(column.key))}
                  </div>
                </th>
              ))}
              {actions && actionsPosition === 'right' && (
                <th
                  scope="col"
                  className={cn(densityConfig.headerPadding, 'text-right text-[11px] font-semibold', colors.textSecondary, 'uppercase tracking-wide', thClassName)}
                >
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className={cn(colors.surface, !tbodyClassName && `divide-y ${colors.border}`, tbodyClassName)}>
            {data.map((row) => {
              // Get row-level formatting rules
              const rowRules = getRowFormattingRules(row);
              const rowStyles = combineStyles(rowRules);
              const rowId = keyExtractor(row);
              const isSelected = selectedRowId !== undefined && selectedRowId !== null && rowId === selectedRowId;
              
              return (
                <tr
                  key={rowId}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={cn(
                    onRowClick ? `cursor-pointer hover:${colors.surfaceHover}` : `hover:${colors.surfaceHover}`,
                    isSelected && 'bg-teal-50 border-l-4 border-l-teal-500',
                    rowStyles,
                    trClassName
                  )}
                >
                {actions && actionsPosition === 'left' && (
                  <td className={cn(densityConfig.cellPadding, 'whitespace-nowrap text-right text-sm font-medium', colors.text, tdClassName)}>
                    <div className="flex items-center flex-nowrap gap-0.5 justify-end">
                      {actions(row)}
                    </div>
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={String(column.key)}
                    className={cn(
                      densityConfig.cellPadding,
                      'text-[13px] leading-snug font-normal',
                      colors.text,
                      column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left',
                      typeof column.width === 'string' ? 'whitespace-nowrap' : 'whitespace-normal sm:whitespace-nowrap',
                      column.maxWidth && 'overflow-hidden text-ellipsis',
                      tdClassName
                    )}
                    style={column.maxWidth ? { maxWidth: column.maxWidth } : undefined}
                  >
                    {renderCell(column, row)}
                  </td>
                ))}
                {actions && actionsPosition === 'right' && (
                  <td className={cn(densityConfig.cellPadding, 'whitespace-nowrap text-right text-sm font-medium', colors.text, tdClassName)}>
                    <div className="flex items-center flex-nowrap gap-1 justify-end">
                      {actions(row)}
                    </div>
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* Footer - Outside scrollable area */}
      {showFooter && (
        <div className={`${colors.surfaceHover} border-t-2 ${colors.border} overflow-x-auto`}>
          <div className="inline-block min-w-max align-middle">
            <table ref={footerTableRef} className={`min-w-max divide-y ${colors.border}`} style={{ tableLayout: 'auto' }}>
              {colgroup}
              <tbody>
                <tr>
                  {actions && actionsPosition === 'left' && (
                    <td
                      className={`${densityConfig.cellPadding} text-sm font-semibold text-right ${colors.textSecondary}`}
                    >
                      {/* Actions column footer is intentionally left empty */}
                    </td>
                  )}
                  {columns.map((column) => {
                    const footerValue = calculateFooterValue(column);
                    const footerLabel = getFooterLabel(column);
                    
                    return (
                      <td
                        key={String(column.key)}
                        className={`${densityConfig.cellPadding} text-sm font-semibold ${colors.text} ${
                          column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'
                        }`}
                      >
                        {footerValue !== null && footerValue !== '' && String(footerValue).trim() !== '' ? (
                          <div className="flex flex-col">
                            {footerLabel && (
                              <span className={`text-xs ${colors.textMuted} uppercase tracking-wider mb-0.5`}>
                                {footerLabel}
                              </span>
                            )}
                            <span>{footerValue}</span>
                          </div>
                        ) : column.footerType === 'custom' && footerValue === null ? (
                          // Empty cell for custom footer that returns null (intentionally empty)
                          <span>&nbsp;</span>
                        ) : (
                          <span className={colors.textMuted}>-</span>
                        )}
                      </td>
                    );
                  })}
                  {actions && actionsPosition === 'right' && (
                    <td 
                      className={`${densityConfig.cellPadding} text-sm font-semibold ${colors.textSecondary} text-right`}
                    >
                      {/* Actions column footer is intentionally left empty */}
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && (
        <div className={`${colors.surface} px-4 py-3 flex items-center justify-between border-t ${colors.border} sm:px-6`}>
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border ${colors.border} text-sm font-medium rounded-md ${colors.textSecondary} ${colors.surface} hover:${colors.surfaceHover} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Previous
            </button>
            <button
              onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border ${colors.border} text-sm font-medium rounded-md ${colors.textSecondary} ${colors.surface} hover:${colors.surfaceHover} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className={`text-sm ${colors.textSecondary}`}>
                Showing{' '}
                <span className="font-medium">
                  {pagination.currentPage === 1
                    ? 1
                    : (pagination.currentPage - 1) * pagination.itemsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                </span>{' '}
                of <span className="font-medium">{pagination.totalItems}</span> results
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {pagination.onItemsPerPageChange && (
                <div className="flex items-center space-x-2">
                  <label className={`text-sm ${colors.textSecondary}`}>Items per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      const newItemsPerPage = parseInt(e.target.value);
                      setItemsPerPage(newItemsPerPage);
                      pagination.onItemsPerPageChange?.(newItemsPerPage);
                    }}
                    className={`border ${colors.border} rounded-md px-3 py-1 text-sm ${colors.text} ${colors.surface} focus:ring-2 focus:ring-teal-500 focus:border-teal-500`}
                  >
                    {pageSizeOptions.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border ${colors.border} ${colors.surface} text-sm font-medium ${colors.textMuted} hover:${colors.surfaceHover} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter((page) => {
                    // Show first page, last page, current page, and pages around current
                    return (
                      page === 1 ||
                      page === pagination.totalPages ||
                      (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1)
                    );
                  })
                  .map((page, index, array) => {
                    // Add ellipsis if there's a gap
                    const showEllipsisBefore = index > 0 && array[index - 1] !== page - 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsisBefore && (
                          <span className={`relative inline-flex items-center px-4 py-2 border ${colors.border} ${colors.surface} text-sm font-medium ${colors.textSecondary}`}>
                            ...
                          </span>
                        )}
                        <button
                          onClick={() => pagination.onPageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === pagination.currentPage
                              ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                              : `${colors.surface} ${colors.border} ${colors.textMuted} hover:${colors.surfaceHover}`
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
                <button
                  onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border ${colors.border} ${colors.surface} text-sm font-medium ${colors.textMuted} hover:${colors.surfaceHover} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;

